// OPT-DEC-02A — Declaración humana de aplicación. Nunca cambia precios ni consulta transacciones.
// PRC-050 (boceto S5): «Seguimiento del resultado» al declarar un precio aplicado.
import React, { useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, ButtonBase, Chip,
  CircularProgress, FormControlLabel, Paper, Radio, RadioGroup, Stack,
  Switch, TextField, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ProaDatePicker from '../../../components/Common/ProaDatePicker';
import {
  getPricingOptimizationDecisions,
  getPricingOptimizationApplications,
  recordPricingOptimizationApplication,
} from '../services/pricingDecisionService';
import type {
  PricingOptimizationApplicationCommand,
  PricingOptimizationApplicationEvent,
} from '../types/pricingDecision';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';
import { openMarginCenterPriceChangeFollowUp } from '../services/marginCenterService';
import { formatDayDate } from './cadenceManagement/cadenceManagementTexts';

// PRC-050: revisión del resultado a 30 · 60 · 90 días de la fecha efectiva u otra fecha.
type FollowReview = 30 | 60 | 90 | 'other';
const FOLLOW_REVIEWS: Array<{ id: FollowReview; label: string }> = [
  { id: 30, label: '30 días' }, { id: 60, label: '60 días' }, { id: 90, label: '90 días' }, { id: 'other', label: 'Otra fecha' },
];
const addDays = (dateOnly: string, days: number): string | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const followErrorText = (error: unknown): string => {
  const detail = (error as { response?: { data?: { detail?: { message?: string } } } })?.response?.data?.detail;
  return detail?.message ?? 'No se pudo abrir el seguimiento del resultado.';
};

const displayAmount = (value: number | null): string => value == null || !Number.isFinite(value)
  ? 'No disponible'
  : pricingNumberFormat({ maximumFractionDigits: 6 }).format(value);
const displayDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Fecha no disponible' : date.toLocaleString('es-ES');
};
const errorText = (error: unknown): string => {
  const code = (error as { response?: { data?: { detail?: { code?: string } } } })
    ?.response?.data?.detail?.code;
  const messages: Record<string, string> = {
    APPLICATION_SCHEMA_NOT_READY: 'La aplicación comercial todavía no está habilitada en este entorno.',
    APPLICATION_CONTEXT_NOT_ELIGIBLE: 'Este contexto aún no admite declaraciones de aplicación.',
    APPLICATION_DECISION_REQUIRED: 'Registra primero una decisión comercial.',
    APPLICATION_DECISION_CONFLICT: 'La decisión ha cambiado. Actualiza esta ficha antes de continuar.',
    APPLICATION_DECISION_NOT_ELIGIBLE: 'Para declarar una aplicación necesitas una decisión de precio vigente.',
    APPLICATION_REVISION_CONFLICT: 'Hay una declaración más reciente. Actualiza esta ficha antes de continuar.',
    APPLICATION_COMMAND_CONFLICT: 'Esta solicitud ya se utilizó con otros datos. Actualiza la ficha.',
    APPLICATION_EVALUATION_NOT_FOUND: 'La evaluación no está disponible para tu cuenta.',
    APPLICATION_INVALID_DETAILS: 'Revisa el precio, la moneda, la unidad y la fecha declarados.',
    APPLICATION_PRICE_DIFFERENCE_REASON_REQUIRED: 'Explica el motivo de utilizar un precio distinto del decidido.',
    APPLICATION_READ_UNAVAILABLE: 'No se pudo recuperar el registro de aplicación.',
    APPLICATION_WRITE_UNCONFIRMED: 'No se pudo confirmar el guardado. Consulta el historial antes de reintentar.',
  };
  return messages[code ?? ''] ?? 'No se pudo completar la operación. Actualiza el estado antes de repetirla.';
};

function ApplicationRow({ event }: { event: PricingOptimizationApplicationEvent }) {
  return <Box sx={{ py: 1.1, borderBottom: '1px solid rgba(148,163,184,0.18)' }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
      <Typography fontWeight={800} color="#edf7fa" variant="body2">
        {event.application_status === 'declared' ? 'Aplicación declarada' : 'No se aplicó'}
        {' · '}Decisión {event.decision_revision}
      </Typography>
      <Typography variant="caption" color="#a4bdd0">{displayDate(event.recorded_at)}</Typography>
    </Stack>
    {event.application_status === 'declared' && <Typography variant="body2" color="#c6e4e8" sx={{ mt: 0.5 }}>
      Precio declarado: {displayAmount(event.applied_price)} {event.currency_code ?? 'Moneda no declarada'}
      {' · '}{event.unit_basis ?? 'Unidad no declarada'}
      {' · '}Desde {event.effective_date ?? 'fecha no disponible'}
    </Typography>}
    {event.reason && <Typography variant="body2" color="#a8c1d1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{event.reason}</Typography>}
  </Box>;
}

interface Props {
  optimizationId: string;
  eligibleObserved: boolean;
  /** PRC-050: la relación tiene ventas anteriores (se puede seguir el resultado). */
  followUpAvailable?: boolean;
}

const OptimizationApplicationSection: React.FC<Props> = ({ optimizationId, eligibleObserved, followUpAvailable = false }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [followOn, setFollowOn] = useState(true);
  const [followReview, setFollowReview] = useState<FollowReview>(60);
  const [followOtherDate, setFollowOtherDate] = useState('');
  const [followRequest, setFollowRequest] = useState<{ application_event_id: string; review_date: string } | null>(null);
  const [openedFollowUp, setOpenedFollowUp] = useState<{ id: string; review_date: string } | null>(null);
  const pendingFollowRef = useRef<string | null>(null);
  const followUp = useMutation({
    mutationFn: (request: { application_event_id: string; review_date: string }) =>
      openMarginCenterPriceChangeFollowUp({ optimization_id: optimizationId, ...request }),
    onSuccess: (result, request) => {
      setFollowRequest(null);
      const id = result.follow_up?.id;
      if (id) setOpenedFollowUp({ id, review_date: request.review_date });
      void queryClient.invalidateQueries({ queryKey: ['margin-center', 'follow-ups'] });
    },
  });
  const decisionKey = ['pricingOptimizationDecisions', optimizationId];
  const applicationKey = ['pricingOptimizationApplications', optimizationId];
  const decisions = useQuery({
    queryKey: decisionKey,
    queryFn: () => getPricingOptimizationDecisions(optimizationId),
    enabled: eligibleObserved,
    retry: 1,
  });
  const applications = useQuery({
    queryKey: applicationKey,
    queryFn: () => getPricingOptimizationApplications(optimizationId),
    enabled: eligibleObserved,
    retry: 1,
  });
  const [choice, setChoice] = useState<'' | 'declared' | 'not_applied'>('');
  const [price, setPrice] = useState('');
  const [date, setDate] = useState('');
  const [currency, setCurrency] = useState('');
  const [unit, setUnit] = useState('');
  const [reason, setReason] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const commandRef = useRef<{ signature: string; command: PricingOptimizationApplicationCommand } | null>(null);

  const mutation = useMutation({
    mutationFn: (command: PricingOptimizationApplicationCommand) =>
      recordPricingOptimizationApplication(optimizationId, command),
    onSuccess: (result) => {
      commandRef.current = null;
      // PRC-050: con el interruptor activado, abrir el seguimiento de esta aplicación.
      const reviewDate = pendingFollowRef.current;
      pendingFollowRef.current = null;
      const event = (result as { event?: { id?: string; application_status?: string } } | undefined)?.event;
      if (reviewDate && event?.id && event.application_status === 'declared') {
        const request = { application_event_id: event.id, review_date: reviewDate };
        setFollowRequest(request);
        followUp.mutate(request);
      }
      setChoice(''); setPrice(''); setDate(''); setCurrency(''); setUnit(''); setReason('');
      setLocalError(null);
      void queryClient.invalidateQueries({ queryKey: applicationKey });
    },
    onError: (failure) => {
      pendingFollowRef.current = null;
      setLocalError(errorText(failure));
      const code = (failure as { response?: { data?: { detail?: { code?: string } } } })
        ?.response?.data?.detail?.code;
      if (code === 'APPLICATION_DECISION_CONFLICT' || code === 'APPLICATION_REVISION_CONFLICT') {
        commandRef.current = null;
        void queryClient.invalidateQueries({ queryKey: decisionKey });
        void queryClient.invalidateQueries({ queryKey: applicationKey });
      }
    },
  });

  const currentDecision = decisions.data?.current ?? null;
  const canDeclare = currentDecision?.choice === 'accept_reference'
    || currentDecision?.choice === 'alternative_price';
  const normalizedPrice = price.trim().replace(',', '.');
  const numberPrice = Number(normalizedPrice);
  const validPrice = /^\d+(?:\.\d{1,6})?$/.test(normalizedPrice)
    && Number.isFinite(numberPrice) && numberPrice > 0 && numberPrice < 100000000000000;
  const cleanCurrency = currency.trim().toUpperCase();
  const cleanUnit = unit.trim();
  const cleanReason = reason.trim();
  const priceDiffers = choice === 'declared' && currentDecision?.decided_price != null
    && validPrice && Math.abs(numberPrice - currentDecision.decided_price) > 0.0000001;
  const now = new Date();
  const localToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
    && !Number.isNaN(new Date(`${date}T12:00:00`).getTime()) && date <= localToday;
  const followActive = choice === 'declared' && followUpAvailable && followOn;
  const followReviewDate = followReview === 'other'
    ? (/^\d{4}-\d{2}-\d{2}$/.test(followOtherDate) ? followOtherDate : null)
    : (validDate ? addDays(date, followReview) : null);
  const followDateValid = !followActive || (followReviewDate != null && validDate && followReviewDate > date
    && followReviewDate <= (addDays(date, 730) ?? ''));
  const canSend = Boolean(eligibleObserved && currentDecision && applications.data
    && choice && !mutation.isPending && !followUp.isPending && cleanReason.length <= 1000 && followDateValid
    && (choice === 'not_applied' ? cleanReason.length > 0
      : canDeclare && (!priceDiffers || cleanReason.length > 0) && validPrice && validDate && /^[A-Z]{3}$/.test(cleanCurrency)
        && cleanUnit.length > 0 && cleanUnit.length <= 80));

  const save = () => {
    if (!canSend || !currentDecision || !applications.data || !choice) return;
    const payload = {
      version: '1.0' as const,
      expected_revision: applications.data.revision,
      decision_revision: currentDecision.revision,
      application_status: choice,
      applied_price: choice === 'declared' ? normalizedPrice : null,
      effective_date: choice === 'declared' ? date : null,
      currency_code: choice === 'declared' ? cleanCurrency : null,
      unit_basis: choice === 'declared' ? cleanUnit : null,
      reason: cleanReason || null,
    };
    const signature = JSON.stringify(payload);
    const retained = commandRef.current;
    const command = retained?.signature === signature ? retained.command : {
      ...payload, command_id: crypto.randomUUID(),
    };
    commandRef.current = { signature, command };
    setLocalError(null);
    setOpenedFollowUp(null);
    setFollowRequest(null);
    followUp.reset();
    pendingFollowRef.current = followActive ? followReviewDate : null;
    mutation.mutate(command);
  };

  const current = applications.data?.current ?? null;
  return <Paper sx={{ bgcolor: '#111d2e', color: '#e2e8f0', border: '1px solid rgba(148,163,184,0.28)', borderRadius: 2, p: { xs: 2, md: 2.3 }, backgroundImage: 'none' }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
      <Box>
        <Typography variant="overline" fontWeight={850} color="#7dd3fc">PASO 3 · APLICACIÓN COMERCIAL</Typography>
        <Typography fontWeight={850} variant="h6" color="#f5faff">¿Se ha utilizado realmente el precio decidido?</Typography>
      </Box>
      <Chip size="small" sx={{ color: '#c5e8fb', bgcolor: '#18354a', fontWeight: 800 }}
        label={current ? current.application_status === 'declared' ? 'Aplicación declarada' : 'No aplicada' : 'Sin aplicación declarada'} />
    </Stack>
    {!eligibleObserved && <Alert severity="info" sx={{ mt: 1.5 }}>La aplicación de contextos prospectivos requiere identificar antes una relación comercial real.</Alert>}
    {eligibleObserved && (decisions.isLoading || applications.isLoading) && <Stack direction="row" gap={1.3} alignItems="center" sx={{ mt: 1.7 }}><CircularProgress size={20} /><Typography variant="body2">Recuperando aplicación…</Typography></Stack>}
    {eligibleObserved && (decisions.isError || applications.isError) && <Alert severity="error" sx={{ mt: 1.5 }} action={<Button color="inherit" onClick={() => { void decisions.refetch(); void applications.refetch(); }}>Reintentar</Button>}>
      {errorText(decisions.error ?? applications.error)}
    </Alert>}
    {eligibleObserved && decisions.data && applications.data && <>
      {current && <Box sx={{ px: 1.6, py: 1, mt: 1.4, bgcolor: '#122d36', border: '1px solid #305766', borderRadius: 1.5 }}>
        <Typography variant="caption" color="#8cdbc9" fontWeight={800}>ÚLTIMA DECLARACIÓN</Typography>
        <ApplicationRow event={current} />
        {current.decision_revision !== currentDecision?.revision && <Typography variant="caption" color="#f7ca89">
          Esta declaración corresponde a una decisión anterior; revisa si sigue vigente.
        </Typography>}
      </Box>}
      {!currentDecision && <Typography variant="body2" color="#a7bece" sx={{ mt: 1.4 }}>Registra primero una decisión comercial en el paso 2.</Typography>}
      {currentDecision && <>
        <RadioGroup value={choice} onChange={(event) => {
          const next = event.target.value as 'declared' | 'not_applied';
          setChoice(next);
          setLocalError(null);
          if (next === 'declared' && currentDecision.decided_price != null) setPrice(String(currentDecision.decided_price));
        }} sx={{ mt: 1.4, display: 'flex', gap: 0.4 }}>
          <FormControlLabel value="declared" control={<Radio size="small" />} label="Sí, declarar aplicación efectiva" disabled={!canDeclare || mutation.isPending} />
          <FormControlLabel value="not_applied" control={<Radio size="small" />} label="No se aplicó esta decisión" disabled={mutation.isPending} />
        </RadioGroup>
        {choice === 'declared' && <Box sx={{ mt: 1.3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4,minmax(0,1fr))' }, gap: 1.2 }}>
          <TextField size="small" label="Precio utilizado" value={price} onChange={(event) => setPrice(event.target.value)} helperText="Confirma el importe real" />
          <ProaDatePicker label="Desde qué fecha" value={date} onChange={setDate} maxDate={new Date().toLocaleDateString('sv-SE')} helperText="Solo fechas presentes o pasadas" />
          <TextField size="small" label="Moneda (3 letras)" value={currency} onChange={(event) => setCurrency(event.target.value.toUpperCase())} inputProps={{ maxLength: 3 }} placeholder="EUR" helperText="Declarada por ti, no inferida" />
          <TextField size="small" label="Unidad comercial" value={unit} onChange={(event) => setUnit(event.target.value)} inputProps={{ maxLength: 80 }} placeholder="pieza, kg, caja…" helperText="Para comparaciones posteriores" />
        </Box>}
        {priceDiffers && <Typography variant="caption" color="#f7ca89" sx={{ display: 'block', mt: 1.2 }}>El precio declarado difiere del decidido: indica el motivo.</Typography>}
        {choice === 'declared' && followUpAvailable && <Box sx={{ mt: 1.5, p: 2, border: '1px solid rgba(148,163,184,0.28)', borderRadius: 2, bgcolor: '#0e1a2b' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box>
              <Typography variant="overline" fontWeight={850} color="#7dd3fc" sx={{ lineHeight: 1.6 }}>SEGUIMIENTO DEL RESULTADO</Typography>
              <Typography fontWeight={850} color="#f5faff">Medir el efecto del cambio</Typography>
            </Box>
            <Switch checked={followOn} onChange={(event) => setFollowOn(event.target.checked)} inputProps={{ 'aria-label': 'Hacer seguimiento del resultado' }} disabled={mutation.isPending} />
          </Stack>
          {followOn && <>
            <Typography variant="body2" fontWeight={700} color="#cbd5e1" sx={{ mt: 1.25, mb: 0.75 }}>Fecha de revisión</Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap>
              {FOLLOW_REVIEWS.map((option) => {
                const on = followReview === option.id;
                const at = option.id !== 'other' && validDate ? addDays(date, option.id) : null;
                return <ButtonBase key={String(option.id)} onClick={() => setFollowReview(option.id)} aria-pressed={on} sx={{
                  border: '1px solid', borderColor: on ? '#38bdf8' : '#334155', borderRadius: 999, px: 1.5, py: 0.6,
                  fontSize: 12.5, fontWeight: 600, color: on ? '#7dd3fc' : '#cbd5e1', bgcolor: on ? 'rgba(56,189,248,.14)' : 'transparent',
                }}>{on && at ? `${option.label} · ${formatDayDate(at)}` : option.label}</ButtonBase>;
              })}
            </Stack>
            {followReview === 'other' && <Box sx={{ mt: 1.2, maxWidth: 260 }}>
              <ProaDatePicker label="Fecha de revisión" value={followOtherDate} onChange={setFollowOtherDate} minDate={validDate ? addDays(date, 1) ?? undefined : undefined} />
            </Box>}
            {!followDateValid && validDate && <Typography variant="caption" color="#f7ca89" sx={{ display: 'block', mt: 0.75 }}>
              {followReview === 'other' && !followOtherDate
                ? 'Elige la fecha de revisión o apaga el seguimiento.'
                : 'La revisión debe ser posterior a la fecha efectiva (como mucho dos años).'}
            </Typography>}
            <Typography variant="body2" fontWeight={700} color="#cbd5e1" sx={{ mt: 1.5 }}>Indicadores que se compararán (antes / después)</Typography>
            <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', rowGap: 0.5, columnGap: 1.5, m: 0, mt: 0.75, fontSize: 13, '& dt': { color: '#94a3b8' }, '& dd': { m: 0, color: '#e2e8f0' } }}>
              <dt>Precio cobrado</dt><dd>ingresos / unidades</dd>
              <dt>Unidades</dt><dd>variación %</dd>
              <dt>Margen</dt><dd>importe y variación %</dd>
              <dt>Ritmo de compra</dt><dd>frente a su frecuencia habitual</dd>
            </Box>
            <Typography variant="caption" color="#9dbacc" sx={{ display: 'block', mt: 1.25 }}>
              Si la relación deja de comprar a su ritmo antes de la fecha, el seguimiento pasa a «Revisión vencida o para hoy».
            </Typography>
          </>}
        </Box>}
        {choice && <TextField size="small" fullWidth multiline minRows={1} sx={{ mt: 1.3 }}
          label={choice === 'not_applied' || priceDiffers ? 'Motivo (obligatorio)' : 'Nota (opcional)'}
          value={reason} onChange={(event) => setReason(event.target.value)} inputProps={{ maxLength: 1000 }} />}
        {choice && <Button variant="contained" disabled={!canSend} onClick={save}
          sx={{ bgcolor: '#77e7d4', color: '#082e34', fontWeight: 850, mt: 1.4, '&:hover': { bgcolor: '#b4faee' } }}>
          {mutation.isPending ? 'Guardando…' : 'Guardar declaración'}
        </Button>}
        {localError && <Alert severity="error" sx={{ mt: 1.2 }}>{localError}</Alert>}
        {followUp.isPending && <Stack direction="row" gap={1} alignItems="center" sx={{ mt: 1.2 }}><CircularProgress size={16} /><Typography variant="body2">Abriendo el seguimiento del resultado…</Typography></Stack>}
        {followUp.isError && followRequest && <Alert severity="warning" sx={{ mt: 1.2 }} action={<Button color="inherit" onClick={() => followUp.mutate(followRequest)}>Reintentar</Button>}>
          La aplicación se ha guardado, pero no se pudo abrir el seguimiento: {followErrorText(followUp.error)}
        </Alert>}
        {openedFollowUp && <Alert severity="success" sx={{ mt: 1.2 }} action={<Button color="inherit" onClick={() => navigate(`/margin-center/control-comercial?case=${encodeURIComponent(openedFollowUp.id)}`)}>Ver seguimiento</Button>}>
          Seguimiento del resultado abierto en Control comercial · revisión el {formatDayDate(openedFollowUp.review_date)}.
        </Alert>}
      </>}
      <Typography variant="caption" color="#9dbacc" sx={{ display: 'block', mt: 1.3 }}>
        Es una declaración humana: no cambia tarifas ni acredita una venta. La comparación con transacciones se habilitará después, con moneda y unidad verificadas.
      </Typography>
      <Accordion disableGutters sx={{ mt: 1.2, bgcolor: '#0e1a2b', color: '#e2e8f0', border: '1px solid #314658', boxShadow: 'none', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: '#9bc5d6' }} />}>
          <Typography variant="body2" fontWeight={800}>Historial de aplicación ({applications.data.history.length})</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {applications.data.history.length === 0 ? <Typography variant="body2" color="#9ab3c6">No hay declaraciones registradas.</Typography>
            : [...applications.data.history].reverse().map((event) => <ApplicationRow key={event.id} event={event} />)}
          {applications.data.history_limited && <Typography variant="caption">Se muestran las 200 declaraciones más recientes.</Typography>}
        </AccordionDetails>
      </Accordion>
    </>}
  </Paper>;
};

export default OptimizationApplicationSection;
